import { useState, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import SurahCard from '../components/SurahCard';
import ResumeCard from '../components/ResumeCard';
import { quranApi } from '../lib/api';
import { useTheme } from '../hooks/useQuran';

// Background images from design guidelines
const LIGHT_BG = 'https://static.prod-images.emergentagent.com/jobs/f3a71348-0cf5-4c42-ade7-1b76aa53f7ec/images/32d417ab17c4b81eb2b264be5eb781b8f5bf05b498e3d7e80e588fccb2c4b1a3.png';
const DARK_BG = 'https://static.prod-images.emergentagent.com/jobs/f3a71348-0cf5-4c42-ade7-1b76aa53f7ec/images/2fddd7e090bb931bf6fad3e32b5a4139901847a4dae761de74a04ff91e79b3ea.png';

export default function HomePage() {
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    loadSurahs();
  }, []);

  const loadSurahs = async () => {
    try {
      setLoading(true);
      const data = await quranApi.getSurahs();
      setSurahs(data);
    } catch (err) {
      console.error('Error loading surahs:', err);
      setError('Failed to load surahs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter surahs based on search - normalize strings for better matching
  const filteredSurahs = surahs.filter((surah, index) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    // Normalize names by removing hyphens, apostrophes, and collapsing double vowels
    const normalize = (str) => str.toLowerCase()
      .replace(/[-']/g, '')
      .replace(/aa/g, 'a')
      .replace(/ee/g, 'e')
      .replace(/ii/g, 'i')
      .replace(/oo/g, 'o')
      .replace(/uu/g, 'u')
      .replace(/\s+/g, ' ')
      .trim();
    
    const normalizedQuery = normalize(query);
    const normalizedName = normalize(surah.surahName);
    const normalizedTranslation = normalize(surah.surahNameTranslation);
    
    return (
      normalizedName.includes(normalizedQuery) ||
      normalizedTranslation.includes(normalizedQuery) ||
      surah.surahName.toLowerCase().includes(query) ||
      surah.surahNameTranslation.toLowerCase().includes(query) ||
      surah.surahNameArabic.includes(searchQuery) ||
      // Also match by surah number
      (index + 1).toString() === query
    );
  });

  const bgImage = resolvedTheme === 'dark' ? DARK_BG : LIGHT_BG;

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero section */}
      <div 
        className="relative overflow-hidden py-16 md:py-24"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
        
        <div className="relative container mx-auto px-4 text-center">
          {/* Bismillah */}
          <p className="font-arabic text-2xl md:text-3xl text-primary mb-4">
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </p>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Ahmad Quran
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-8">
            Free Quran Learning App - Read, listen, and memorize with translations, tafsir, and beautiful recitations
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search surahs by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base bg-background/80 backdrop-blur"
              data-testid="surah-search"
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Resume reading card */}
        <div className="mb-8">
          <ResumeCard />
        </div>

        {/* Section header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl font-semibold">All Surahs</h2>
          <span className="text-sm text-muted-foreground">
            {filteredSurahs.length} of 114 surahs
          </span>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="text-center py-24">
            <p className="text-destructive mb-4">{error}</p>
            <button 
              onClick={loadSurahs}
              className="text-primary underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Surah grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSurahs.map((surah, index) => (
              <div 
                key={index}
                className="animate-fade-in"
                style={{ animationDelay: `${(index % 12) * 0.05}s` }}
              >
                <SurahCard surah={surah} index={surahs.indexOf(surah)} />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filteredSurahs.length === 0 && (
          <div className="text-center py-24">
            <p className="text-muted-foreground">No surahs found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    </div>
  );
}
